import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-circle.svg';
import { ReactComponent as SuccessIcon } from '../../../../assets/icons/success-tick.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { useAppDispatch } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { fetchRequestApprovedAccount } from '../../../../redux/slices/RequestApprovedAccountSlice';
import { fetchRequestApprovedStation } from '../../../../redux/slices/RequestApprovedStationSlice';
import { fetchRequestPendingAccount } from '../../../../redux/slices/RequestPendingAccountSlice';
import { fetchRequestPendingStation } from '../../../../redux/slices/RequestPendingStationSlice';
import { fetchRequestRejectedAccount } from '../../../../redux/slices/RequestRejectedAccountSlice';
import { fetchRequestRejectedStation } from '../../../../redux/slices/RequestRejectedStationSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../../shared-components/multi-select-field/MultiSelectField';
import RejectReasonField from '../../../../shared-components/reject-reason/RejectReasonField';
import SelectFiled from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import penaltyManagementApi from '../../../../utils/api/penalty-management';
import AuditLog from '../AuditLog';

interface UserAddInterface {
  accountCustomId: string;
  ceilingType: string;
  ceilingValue: number;
  triggerMessage: string[];
  effectiveFrom: string;
  effectiveEnd: string;
  dynamicPricing: string[];
  stationId: string[];
  stationNames: string[];
  rules: RuleInterface[];
  penaltyFeature: boolean;
  facilityRequest: boolean;
  accountName: string;
  stationName?: string;
}

interface RuleInterface {
  from: string;
  to: string;
  mode: string;
  amount: string;
}

/**
 * Normalize a value to an array
 * @param value - string or string array
 * @returns - array of strings
 */
const normalizeToArray = (value?: string | string[]): string[] => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

interface ModalBoxDetail {
  title: String;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * Edit Penalty
 * @returns
 */
function ViewOverrides({
  closeModal,
  currentData,
  type,
  list,
}: {
  closeModal: (value: string) => void;
  currentData?: any;
  type: 'Approved' | 'Pending' | 'Rejected';
  list?: 'account' | 'station';
}) {
  const [buttonStatus] = useState(false);
  const [ruleErrors] = useState<{
    [index: number]: { from: boolean; to: boolean };
  }>({});
  const dispatch = useAppDispatch();
  const { t } = useTranslation('penaltyManagement');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage] = useState(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [rejectError, setRejectError] = useState<string>('');
  const [penaltyDetails, setPenaltyDetails] = useState<UserAddInterface>({
    accountCustomId: '',
    accountName: '',
    ceilingType: '',
    stationName: '',
    ceilingValue: 0,
    triggerMessage: [],
    effectiveFrom: '',
    effectiveEnd: '',
    dynamicPricing: [],
    stationId: [],
    stationNames: [],
    rules: [{ from: '', to: '', mode: '', amount: '' }],
    penaltyFeature: false,
    facilityRequest: false,
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });
  /**
   * Close modal function
   */
  const handleClose = () => {
    setModalBoxStatus('close');
    closeModal('');
  };

  /**
   * Show success notification
   * @param title - Notification title
   */
  const showSuccess = (notificationTitle: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      content: (
        <SuccessNotification
          title={notificationTitle}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={handleClose} // ✅ close + refresh here
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Normal Input Handler
   */
  const handleInputChange = (name: string, value: string | number | boolean | string[]) => {
    setPenaltyDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const allModes = [
    { label: t('grace'), value: 'grace' },
    { label: t('flatRate'), value: 'flatRate' },
    { label: t('perMinute'), value: 'perMinute' },
  ];

  /**
   * Update rule row field value
   */
  const updateRule = (index: number, name: keyof RuleInterface, value: string) => {
    const rules = [...penaltyDetails.rules];

    rules[index][name] = value;

    setPenaltyDetails((previous) => ({
      ...previous,
      rules,
    }));
  };

  /**
   * Filter available mode options based on already selected modes
   */
  const getAvailableModes = (currentIndex: number) => {
    const selectedModes = penaltyDetails.rules.map((r) => r.mode).filter(Boolean);

    return allModes.filter((m) => {
      // Allow any mode if it's already selected for the current index (editing case)
      if (penaltyDetails.rules[currentIndex].mode === m.value) return true;
      // Make "Grace" unique: if already selected anywhere else, hide it
      if (m.value === 'grace' && selectedModes.includes('grace')) {
        return false;
      }

      // Allow Flat Rate & Per Minute multiple times
      return true;
    });
  };

  /**
   * Submit payload to API
   */
  /**
   * handle Action Reject
   */
  const handleActionReject = async (reason: string, data: any) => {
    try {
      if (list === 'account') {
        await penaltyManagementApi.createAccountApprovedRejectedPermission({
          requestStatus: 'rejected',
          reason,
          penaltyIds: [data._id],
        });
      } else {
        await penaltyManagementApi.createApprovedRejectedPermission({
          status: 'rejected',
          reason,
          penaltyIds: [data._id],
        });
      }

      if (list === 'account') {
        dispatch(
          fetchRequestPendingAccount({ page: DefaultApiLimit.page, limit: DefaultApiLimit.limit }),
        );
        dispatch(
          fetchRequestRejectedAccount({ page: DefaultApiLimit.page, limit: DefaultApiLimit.limit }),
        );
      } else {
        dispatch(
          fetchRequestPendingStation({
            page: DefaultApiLimit.page,
            limit: DefaultApiLimit.limit,
          }),
        );
        dispatch(
          fetchRequestRejectedStation({ page: DefaultApiLimit.page, limit: DefaultApiLimit.limit }),
        );
      }
      showSuccess(t('rejectionSuccess'));
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * handle Action Approve
   */
  const handleActionApprove = async (selectedItems: string) => {
    try {
      if (list === 'account') {
        await penaltyManagementApi.createAccountApprovedRejectedPermission({
          requestStatus: 'approved',
          penaltyIds: [selectedItems],
        });
      } else {
        await penaltyManagementApi.createApprovedRejectedPermission({
          status: 'approved',
          penaltyIds: [selectedItems],
        });
      }

      if (list === 'account') {
        dispatch(
          fetchRequestPendingAccount({ page: DefaultApiLimit.page, limit: DefaultApiLimit.limit }),
        );
        dispatch(
          fetchRequestApprovedAccount({ page: DefaultApiLimit.page, limit: DefaultApiLimit.limit }),
        );
      } else {
        dispatch(
          fetchRequestPendingStation({
            page: DefaultApiLimit.page,
            limit: DefaultApiLimit.limit,
          }),
        );
        dispatch(
          fetchRequestApprovedStation({ page: DefaultApiLimit.page, limit: DefaultApiLimit.limit }),
        );
      }
      showSuccess(t('approvalSuccessMessage'));
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    if (!currentData?._id) return;

    /**
     *fetchOverrideDetails
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const fetchOverrideDetails = async (id: any) => {
      try {
        setIsLoading(true);

        let response;

        if (list === 'account') {
          response = await penaltyManagementApi.viewSingleAccountOverrideDetails(id);
        } else {
          response = await penaltyManagementApi.viewSingleStationOverrideDetails(id);
        }

        const data = response?.data || {};

        setPenaltyDetails({
          accountCustomId: data.accountCustomId ?? '',
          stationId: normalizeToArray(data.stationId),
          stationNames: normalizeToArray(data.stationNames),
          stationName: data.stationName ?? '',
          accountName: data.accountName ?? '',
          ceilingType: data.ceilingType ?? '',
          ceilingValue: data.ceilingValue ?? 0,
          triggerMessage: normalizeToArray(data.triggerMessage),
          dynamicPricing: normalizeToArray(data.dynamicPricing),
          effectiveFrom: ConvertUTCtoDate(data.effectiveFrom) ?? '',
          effectiveEnd: ConvertUTCtoDate(data.effectiveTo) ?? '',
          rules:
            Array.isArray(data.durationAmountList) && data.durationAmountList.length
              ? data.durationAmountList.map((item: any) => ({
                  from: String(item.from),
                  to: String(item.to),
                  mode: item.mode,
                  amount: String(item.amount),
                }))
              : [{ from: '', to: '', mode: '', amount: '' }],
          penaltyFeature: Boolean(data.penaltyFeature),
          facilityRequest: Boolean(data.facilityRequest),
        });

        setRejectReason(data.reason);
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: error?.response?.status || 500,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchOverrideDetails(currentData._id);
  }, [currentData._id, list]);

  return (
    <div className="override-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <p className={`heading ${type}`}>
        {t(type.toLowerCase())} {type === 'Rejected' && <CloseIcon />}
        {type === 'Approved' && <SuccessIcon className="icon" />}
      </p>
      {isLoading ? (
        <div className="loader">
          <BatteryLoader />
        </div>
      ) : (
        <div className="override-container view">
          <div className="override-left">
            <div className="inputs-container">
              <div className="input-side-by-side">
                {' '}
                <CustomInput
                  name="accountCustomId"
                  inputLabel={t('accountId')}
                  type="text"
                  placeholder={t('enterValue')}
                  value={penaltyDetails.accountCustomId}
                  onChangeValue={handleInputChange}
                  error={errorMessage && penaltyDetails.accountCustomId === ''}
                  errorMessage={t('required')}
                  disabled
                  valueRequired
                />{' '}
                <CustomInput
                  name="accountName"
                  inputLabel={t('accountName')}
                  type="text"
                  placeholder={t('enterValue')}
                  value={penaltyDetails.accountName}
                  onChangeValue={handleInputChange}
                  disabled
                />
                {/* {list !== 'account' && (
                  <MultiSelectDropdown
                    name="stationId"
                    title={t('stationId')}
                    selectedValues={penaltyDetails.stationId}
                    options={penaltyDetails?.stationId?.map((station) => ({
                      label: station,
                      value: station,
                    }))}
                    onSelectionChange={handleInputChange}
                    placeholder={t('selectStationPlaceholder')}
                    placeholderFilled={t('selectStation')}
                    error={errorMessage || penaltyDetails.stationId.length === 0}
                    errorMessage={t('required')}
                    disabled
                    valueRequired
                  />
                )} */}
                
                  {list !== 'account' && (
                    <CustomInput
                      name="stationId"
                      inputLabel={t('stationName')}
                      type="text"
                      placeholder={t('enterValue')}
                      value={penaltyDetails.stationName || ''}
                      onChangeValue={handleInputChange}
                      disabled
                      valueRequired
                    />
                  )}
                
                <MultiSelectDropdown
                  name="dynamicPricing"
                  title={t('dynamicPricing')}
                  selectedValues={penaltyDetails.dynamicPricing}
                  onSelectionChange={handleInputChange}
                  placeholder={t('selectOption')}
                  placeholderFilled={t('options')}
                  options={[
                    { label: t('base'), value: 'base' },
                    { label: t('peak'), value: 'peak' },
                    { label: t('offPeak'), value: 'offpeak' },
                  ]}
                  search={false}
                  multiSelectDisable={false}
                  valueRequired
                  fontSize="small"
                  error={errorMessage && penaltyDetails.dynamicPricing.length === 0}
                  errorMessage={t('required')}
                  disabled
                  selectedChips
                />
                <SelectFiled
                  name="ceilingType"
                  inputLabel={t('ceilingType')}
                  selectedValue={penaltyDetails.ceilingType}
                  placeholder={t('selectCeilingType')}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={errorMessage && penaltyDetails.ceilingType === ''}
                  errorMessage={t('required')}
                  options={[
                    { label: t('fixedAmount'), value: 'fixedAmount' },
                    { label: t('percentage'), value: 'percentage' },
                  ]}
                  disabled
                />
                <CustomInput
                  name="amount"
                  inputLabel={t('maxCeilingValue')}
                  type="number"
                  placeholder={t('enterValue')}
                  value={penaltyDetails.ceilingValue}
                  onChangeValue={handleInputChange}
                  error={errorMessage && penaltyDetails.ceilingValue <= 0}
                  errorMessage={t('required')}
                  disabled
                  valueRequired
                />
              </div>
              <MultiSelectDropdown
                name="triggerMessage"
                title={t('triggerMessage')}
                selectedValues={penaltyDetails.triggerMessage}
                onSelectionChange={handleInputChange}
                placeholder={t('selectOption')}
                placeholderFilled={t('options')}
                options={[
                  { label: t('suspendedEVSE'), value: 'SuspendedEVSE' },
                  { label: t('suspendedEV'), value: 'SuspendedEV' },
                  { label: t('faulted'), value: 'Faulted' },
                  { label: t('finishing'), value: 'Finishing' },
                ]}
                search={false}
                multiSelectDisable={false}
                valueRequired
                fontSize="small"
                selectedChips
                error={errorMessage && penaltyDetails.triggerMessage.length === 0}
                errorMessage={t('required')}
                disabled
              />
              <div className="date-box-content">
                <CustomInput
                  name="effectiveFrom"
                  inputLabel={t('startDate')}
                  type="text"
                  placeholder={t('enterValue')}
                  value={penaltyDetails.effectiveFrom}
                  onChangeValue={handleInputChange}
                  error={errorMessage && penaltyDetails.effectiveFrom === ''}
                  errorMessage={t('required')}
                  disabled
                  valueRequired
                />{' '}
                <CustomInput
                  name="effectiveEnd"
                  inputLabel={t('endDate')}
                  type="text"
                  placeholder={t('enterValue')}
                  value={penaltyDetails.effectiveEnd}
                  onChangeValue={handleInputChange}
                  error={errorMessage && penaltyDetails.effectiveEnd === ''}
                  errorMessage={t('required')}
                  disabled
                  valueRequired
                />
              </div>
            </div>
            <div className="pricing-wrap">
              {penaltyDetails.rules.map((rule, index) => (
                <div className="rule-box-container">
                  <div className="rule-box" key={index}>
                    <CustomInput
                      name="from"
                      inputLabel={t('fromMins')}
                      placeholder={t('enterFromMins')}
                      type="number"
                      value={rule.from}
                      onChangeValue={(name, value) =>
                        updateRule(index, name as keyof RuleInterface, String(value))
                      }
                      error={ruleErrors[index]?.from}
                      errorMessage={
                        rule.from.length < 0 ? t('fillBothRulesError') : t('invalidRangeError')
                      }
                      disabled
                    />{' '}
                    <CustomInput
                      name="to"
                      inputLabel={t('toMins')}
                      placeholder={t('enterToMins')}
                      type="number"
                      value={rule.to}
                      onChangeValue={(name, value) =>
                        updateRule(index, name as keyof RuleInterface, String(value))
                      }
                      error={ruleErrors[index]?.to}
                      errorMessage={
                        rule.to.length < 0 ? t('fillBothRulesError') : t('invalidRangeError')
                      }
                      disabled
                    />
                    <SelectFiled
                      name="mode"
                      inputLabel={t('mode')}
                      placeholder={t('enterMode')}
                      selectedValue={rule.mode}
                      onChangeValue={(name, value) =>
                        updateRule(index, name as keyof RuleInterface, String(value))
                      }
                      options={getAvailableModes(index)}
                      disabled
                    />
                    <CustomInput
                      name="amount"
                      inputLabel={t('amount')}
                      placeholder={t('enterValue')}
                      type="number"
                      value={rule.amount}
                      onChangeValue={(name, value) =>
                        updateRule(index, name as keyof RuleInterface, String(value))
                      }
                      disabled={rule.mode === 'grace'}
                    />
                  </div>
                </div>
              ))}
            </div>
            {type === 'Rejected' && (
              <div>
                <RejectReasonField
                  label={t('rejectReasonLabel')}
                  placeholder={t('rejectReasonPlaceholder')}
                  value={rejectReason}
                  onChange={setRejectReason}
                  disabled
                />
              </div>
            )}
            {type === 'Pending' && (
              <>
                <div>
                  <RejectReasonField
                    label={t('rejectReasonLabel')}
                    placeholder={t('rejectReasonPlaceholder')}
                    value={rejectReason}
                    onChange={(value) => {
                      setRejectReason(value);
                      if (rejectError) {
                        setRejectError('');
                      }
                    }}
                    error={Boolean(rejectError)}
                    errorMessage={rejectError}
                    valueRequired
                  />
                </div>
                <div className="button-row">
                  <MyButton
                    label={t('reject')}
                    buttonType="error"
                    onClickFunc={() => {
                      if (!rejectReason?.trim()) {
                        setRejectError(t('reasonRequired'));
                        return;
                      }

                      handleActionReject(rejectReason, currentData);
                    }}
                    disabled={buttonStatus}
                  />
                  <MyButton
                    label={t('approve')}
                    buttonType="submit"
                    onClickFunc={() => handleActionApprove(currentData._id)}
                    loading={buttonStatus}
                  />
                </div>
              </>
            )}
          </div>
          <div className="AuditLog">
            {currentData.id === 'okay' && (
              <AuditLog
                currentData={{
                  id: currentData._id,
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

ViewOverrides.defaultProps = {
  currentData: null,
  list: null,
};
export default ViewOverrides;
